import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./projects.css";
import { BASE_URL } from "../apiConfig";
import { Funnel, Eye, Search, ChevronLeft, ChevronDown, Trash2 } from "lucide-react";
import Dropdown from "../Architect/dropdown";

const ProjectsHistory = () => {
  const { roleId } = useParams();

  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const [clientId, setClientId] = useState("");
  const [clientName, setClientName] = useState("");
  const [projectName, setProjectName] = useState("");
  const [clientImage, setClientImage] = useState("");
  const [filterStatus, setFilterStatus] = useState("All Status");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const selectRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const fetchClientDetails = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        if (!token) {
          throw new Error("Authentication token not found");
        }

        const response = await fetch(
          `${BASE_URL}/client/get_single_role_details?role_id=${roleId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch client details");
        }

        const data = await response.json();
        console.log("Client Details API Response:", data);

        if (data && data.client_details) {
          setClientName(
            data.client_details.name ||
              data.client_details.display_name ||
              "Client"
          );
          setClientId(data.client_id);
          setClientImage(data.client_details.image || "");
        } else {
          console.log("No client details received or empty data");
        }
      } catch (err) {
        console.error("Error fetching client details:", err);
        setError(err.message);
      }
    };

    const fetchProjects = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        if (!token) {
          throw new Error("Authentication token not found");
        }

        const response = await fetch(`${BASE_URL}/project/get_all_projects`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ jobrole_id: roleId }),
        });

        if (!response.ok) {
          throw new Error("Failed to fetch projects");
        }

        const data = await response.json();
        console.log("Projects API Response:", data);

        if (data && data.length > 0) {
          const transformedProjects = data.map((project) => ({
            id: project.id,
            name: project.name,
            phase: project.project_phase
              ? project.project_phase.charAt(0).toUpperCase() +
                project.project_phase.slice(1)
              : "In Progress",
            status: project.project_status || "started",
          }));
          console.log("Transformed projects:", transformedProjects);
          setProjects(transformedProjects);
        } else {
          console.log("No projects data received or empty array");
          setProjects([]);
        }
      } catch (err) {
        console.error("Error fetching projects:", err);
        setError(err.message);
      }
    };

    if (roleId) {
      setLoading(true);
      fetchClientDetails()
        .then(() => fetchProjects())
        .catch((err) => {
          console.error("Error in fetch sequence:", err);
          setError(err.message);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      console.error("No roleId available");
      setError("Role ID is missing");
      setLoading(false);
    }
  }, [roleId]);

  const handleGoBack = () => {
    navigate(`/architect`);
  };

  const handleStartNewProject = () => {
    navigate(`/architect/${clientId}/${roleId}/new-project`, {
      state: { clientName },
    });
  };

  const handleViewProject = (projectId, projectName) => {
    navigate(`/architect/${clientId}/${roleId}/${projectId}/edit-project`, {
      state: { projectName, clientName },
    });
  };

  const handleDeleteClick = (project) => {
    setProjectToDelete(project);
    setDeleteConfirmText("");
    setShowDeleteModal(true);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setProjectToDelete(null);
    setDeleteConfirmText("");
    setIsDeleting(false);
  };

  const handleDeleteConfirm = async () => {
    if (
      !projectToDelete ||
      deleteConfirmText !== projectToDelete.name.trim()
    ) {
      return;
    }

    const token = localStorage.getItem("accessToken");
    if (!token) {
      setError("Authentication token not found");
      return;
    }

    try {
      setIsDeleting(true);
      const response = await fetch(
        `${BASE_URL}/project/archive_project?project_id=${projectToDelete.id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to delete project");
      }

      setProjects((prev) =>
        prev.filter((project) => project.id !== projectToDelete.id),
      );
      cancelDelete();
    } catch (err) {
      console.error("Error deleting project:", err);
      setError(err.message);
      setIsDeleting(false);
    }
  };

  const isDeleteConfirmed =
    projectToDelete &&
    deleteConfirmText === projectToDelete.name.trim();

  const filteredProjects = projects.filter((project) => {
    if (
      filterStatus !== "All Status" &&
      project.phase.toLowerCase() !== filterStatus.toLowerCase()
    ) {
      return false;
    }
    if (
      searchQuery &&
      !project.name.toLowerCase().includes(searchQuery.toLowerCase())
    ) {
      return false;
    }
    return true;
  });

  const handleWrapperMouseDown = (e) => {
    e.preventDefault();

    // Toggle the dropdown state
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);

    // If opening, focus and programmatically show dropdown
    if (newIsOpen) {
      selectRef.current.focus();
      // Small delay to ensure focus is set
      setTimeout(() => {
        selectRef.current.showPicker?.(); // Modern browsers
      }, 10);
    }
  };

  const handleChange = (e) => {
    setFilterStatus(e.target.value);
    setIsOpen(false); // Close when option selected
  };

  // Handle select focus (when opened via keyboard or other means)
  const handleFocus = () => {
    setIsOpen(true);
  };

  // Handle select blur (when focus lost)
  const handleBlur = () => {
    // Small delay to handle option selection
    setTimeout(() => {
      setIsOpen(false);
    }, 100);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <div className="client-sidebar"></div>
      <div className="project-container">
        {error ? (
          <div className="project-error">Error: {error}</div>
        ) : (
          <div className="project-history-section">
            {/* Header Section - Always visible */}
            <div className="project-header">
              <div className="project-back-and-title">
                <button className="project-back-button" onClick={handleGoBack}>
                  <span className="project-back-icon">
                    <ChevronLeft />
                  </span>
                </button>
                <div className="project-company-info">
                  <div className="project-logo">
                    {loading ? (
                      <div className="skeleton skeleton-logo"></div>
                    ) : clientImage ? (
                      <img
                        src={clientImage}
                        alt={clientName}
                        className="project-client-logo"
                      />
                    ) : (
                      <div className="project-client-logo-placeholder">
                        {clientName.charAt(0)}
                      </div>
                    )}
                  </div>
                  <h1 className="project-company-title">
                    {loading ? (
                      <div className="skeleton skeleton-client-name"></div>
                    ) : (
                      clientName || "Client"
                    )}
                  </h1>
                </div>
              </div>
              <button
                className={`project-new-button ${loading ? 'loading' : ''}`}
                onClick={handleStartNewProject}
                disabled={loading}
              >
                {loading ? (
                  <div className="skeleton skeleton-button-text"></div>
                ) : (
                  "Start New Project"
                )}
              </button>
            </div>

            {/* Section Title */}
            <h2 className="project-section-title">
              {loading ? (
                <div className="skeleton skeleton-section-title"></div>
              ) : (
                "Projects History"
              )}
            </h2>

            {/* Filters Section */}
            <div className="project-filters-container">
              <div className="project-filter-section">
                <span className="project-filter-icon">
                  <Funnel size="16px" />
                </span>
                <span className="project-filter-label">Filters</span>
                <div className="project-dropdown">
                  {loading ? (
                    <div className="skeleton skeleton-dropdown"></div>
                  ) : (
                    <Dropdown
                      filterStatus={filterStatus}
                      setFilterStatus={setFilterStatus}
                    />
                  )}
                </div>
              </div>
              <div className="project-search-section">
                {loading ? (
                  <div className="skeleton skeleton-search"></div>
                ) : (
                  <>
                    <input
                      type="text"
                      placeholder="Search Project...."
                      className="project-search-input"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <button className="project-search-button">
                      <span className="project-search-icon">
                        <Search size="16px" />
                      </span>
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Cards Section */}
            {loading ? (
              <div className="project-cards-container">
                {[...Array(4)].map((_, idx) => (
                  <div className="project-card skeleton-card" key={idx}>
                    <div className="project-card-header">
                      <div className="skeleton skeleton-title"></div>
                      <div className="skeleton skeleton-logo"></div>
                    </div>
                    <div className="project-card-status">
                      <div className="skeleton skeleton-phase"></div>
                    </div>
                    <div className="skeleton skeleton-button"></div>
                  </div>
                ))}
              </div>
            ) : filteredProjects.length === 0 ? (
              <div className="project-no-results">No projects found</div>
            ) : (
              <div className="project-cards-container">
                {filteredProjects.map((project) => (
                  <div className="project-card" key={project.id}>
                    <div className="project-card-header">
                      <h3 className="project-card-title">{project.name}</h3>
                      <div className="project-card-logo">
                        {clientImage ? (
                          <img
                            src={clientImage}
                            alt={clientName}
                            className="project-client-logo-small"
                          />
                        ) : (
                          <div className="project-client-logo-placeholder-small">
                            {clientName.charAt(0)}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="project-card-status">
                      <span className="project-phase-label">Phase: </span>
                      <span className="project-phase-value">
                        {project.phase}
                      </span>
                      <span
                        className={`project-status-indicator ${
                          project.phase === "In Progress"
                            ? "progress"
                            : project.phase === "completed"
                            ? "completed"
                            : "completed"
                        }`}
                      ></span>
                    </div>
                    <div className="project-card-actions">
                      <button
                        className="project-view-button"
                        onClick={() =>
                          handleViewProject(project.id, project.name)
                        }
                      >
                        <span className="project-view-icon">
                          <Eye size="16px" style={{ marginTop: "3px" }} />
                        </span>
                        View
                      </button>
                      <button
                        className="project-delete-button"
                        onClick={() => handleDeleteClick(project)}
                      >
                        <span className="project-view-icon">
                          <Trash2 size="16px" style={{ marginTop: "3px" }} />
                        </span>
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {showDeleteModal && (
              <div className="project-delete-modal-overlay">
                <div className="project-delete-modal-container">
                  <div className="project-delete-modal-content">
                    <h3>Delete project?</h3>
                    <p>
                      This will archive{" "}
                      <strong>{projectToDelete?.name}</strong>. This action
                      cannot be undone.
                    </p>
                    <label
                      className="project-delete-confirm-label"
                      htmlFor="delete-confirm-input"
                    >
                      Type <strong>{projectToDelete?.name}</strong> to confirm
                    </label>
                    <input
                      id="delete-confirm-input"
                      type="text"
                      className="project-delete-confirm-input"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder={`Type ${projectToDelete?.name || "project name"}`}
                      autoComplete="off"
                    />
                    <div className="project-delete-modal-buttons">
                      <button
                        type="button"
                        className="project-delete-modal-button project-delete-cancel"
                        onClick={cancelDelete}
                        disabled={isDeleting}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="project-delete-modal-button project-delete-confirm"
                        onClick={handleDeleteConfirm}
                        disabled={!isDeleteConfirmed || isDeleting}
                      >
                        {isDeleting ? "Deleting..." : "Delete"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default ProjectsHistory;